import React, { useState } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { FileText, Search, Plus, Edit, Trash2, Pin, Calendar, User } from 'lucide-react'
import PageHeader from '@/components/PageHeader'

const Notes = () => {
  const [selectedNote, setSelectedNote] = useState(null)

  return (
    <div className="space-y-6">
      {/* Header */}
      <PageHeader
        title="Consultant Notes"
        subtitle="Manage your client notes and observations"
        actions={(
          <>
            <div className="relative hidden sm:block">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4" />
              <input
                type="text"
                placeholder="Search notes..."
                className="pl-10 pr-4 py-1.5 border rounded-lg text-sm focus:outline-none focus:ring-2"
              />
            </div>
            <Button size="sm" variant="ghost" className="">
              <Plus className="h-4 w-4 mr-2" />
              New Note
            </Button>
          </>
        )}
      />

      {/* Notes Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <Card>
          <CardContent className="p-6 text-center">
            <div className="text-3xl font-semibold text-fitness-primary mb-2">47</div>
            <p className="text-slate-600">Total Notes</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6 text-center">
            <div className="text-3xl font-semibold text-blue-600 mb-2">12</div>
            <p className="text-slate-600">This Week</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6 text-center">
            <div className="text-3xl font-semibold text-green-600 mb-2">8</div>
            <p className="text-slate-600">Pinned</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6 text-center">
            <div className="text-3xl font-semibold text-orange-600 mb-2">24</div>
            <p className="text-slate-600">Clients</p>
          </CardContent>
        </Card>
      </div>

      {/* Main Content */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Notes List */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <FileText className="h-5 w-5 text-fitness-primary" />
                <span>Recent Notes</span>
              </div>
              <div className="flex space-x-2">
                <Button variant="outline" size="sm">
                  All
                </Button>
                <Button variant="outline" size="sm">
                  Pinned
                </Button>
                <Button variant="outline" size="sm">
                  Recent
                </Button>
              </div>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {/* Sample Note 1 */}
              <div 
                className={`p-4 border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors cursor-pointer ${
                  selectedNote === 1 ? 'border-fitness-primary bg-fitness-primary/5' : ''
                }`}
                onClick={() => setSelectedNote(1)}
              >
                <div className="flex items-start justify-between mb-2">
                  <div className="flex items-center space-x-2">
                    <Pin className="h-4 w-4 text-orange-500" />
                    <h4 className="font-medium text-slate-900">Sarah's Progress Review</h4>
                  </div>
                  <div className="flex items-center space-x-1">
                    <Button variant="ghost" size="sm">
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button variant="ghost" size="sm">
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
                <p className="text-sm text-slate-600 mb-3">
                  Excellent progress on weight loss goals. Lost 12 lbs in 8 weeks. 
                  Needs to focus more on strength training...
                </p>
                <div className="flex items-center space-x-4 text-xs text-slate-500">
                  <div className="flex items-center">
                    <User className="h-3 w-3 mr-1" />
                    Sarah Johnson
                  </div>
                  <div className="flex items-center">
                    <Calendar className="h-3 w-3 mr-1" />
                    2 days ago
                  </div>
                </div>
              </div>

              {/* Sample Note 2 */}
              <div 
                className={`p-4 border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors cursor-pointer ${
                  selectedNote === 2 ? 'border-fitness-primary bg-fitness-primary/5' : ''
                }`}
                onClick={() => setSelectedNote(2)}
              >
                <div className="flex items-start justify-between mb-2">
                  <h4 className="font-medium text-slate-900">Mike's Workout Modifications</h4>
                  <div className="flex items-center space-x-1">
                    <Button variant="ghost" size="sm">
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button variant="ghost" size="sm">
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
                <p className="text-sm text-slate-600 mb-3">
                  Modified workout plan due to lower back issues. Reduced deadlift weight 
                  and added more core strengthening exercises...
                </p>
                <div className="flex items-center space-x-4 text-xs text-slate-500">
                  <div className="flex items-center">
                    <User className="h-3 w-3 mr-1" />
                    Mike Davis
                  </div>
                  <div className="flex items-center">
                    <Calendar className="h-3 w-3 mr-1" />
                    5 days ago
                  </div>
                </div>
              </div>

              {/* Sample Note 3 */}
              <div 
                className={`p-4 border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors cursor-pointer ${
                  selectedNote === 3 ? 'border-fitness-primary bg-fitness-primary/5' : ''
                }`}
                onClick={() => setSelectedNote(3)}
              >
                <div className="flex items-start justify-between mb-2">
                  <div className="flex items-center space-x-2">
                    <Pin className="h-4 w-4 text-orange-500" />
                    <h4 className="font-medium text-slate-900">Emma's Nutrition Plan Update</h4>
                  </div>
                  <div className="flex items-center space-x-1">
                    <Button variant="ghost" size="sm">
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button variant="ghost" size="sm">
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
                <p className="text-sm text-slate-600 mb-3">
                  Updated meal plan to include more plant-based proteins. Client reported 
                  digestive issues with whey protein...
                </p>
                <div className="flex items-center space-x-4 text-xs text-slate-500">
                  <div className="flex items-center">
                    <User className="h-3 w-3 mr-1" />
                    Emma Wilson
                  </div>
                  <div className="flex items-center">
                    <Calendar className="h-3 w-3 mr-1" />
                    1 week ago
                  </div>
                </div>
              </div>

              {/* Sample Note 4 */}
              <div 
                className={`p-4 border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors cursor-pointer ${
                  selectedNote === 4 ? 'border-fitness-primary bg-fitness-primary/5' : ''
                }`}
                onClick={() => setSelectedNote(4)}
              >
                <div className="flex items-start justify-between mb-2">
                  <h4 className="font-medium text-slate-900">John's Cardio Assessment</h4>
                  <div className="flex items-center space-x-1">
                    <Button variant="ghost" size="sm">
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button variant="ghost" size="sm">
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
                <p className="text-sm text-slate-600 mb-3">
                  Significant improvement in cardiovascular endurance. Can now complete 
                  30-minute HIIT sessions without excessive fatigue...
                </p>
                <div className="flex items-center space-x-4 text-xs text-slate-500">
                  <div className="flex items-center">
                    <User className="h-3 w-3 mr-1" />
                    John Smith
                  </div>
                  <div className="flex items-center">
                    <Calendar className="h-3 w-3 mr-1" />
                    1 week ago
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Note Editor/Details */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <Edit className="h-5 w-5 text-fitness-primary" />
              <span>{selectedNote ? 'Edit Note' : 'Create New Note'}</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {selectedNote ? (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Client
                  </label>
                  <select className="w-full p-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-fitness-primary">
                    <option>Sarah Johnson</option>
                    <option>Mike Davis</option>
                    <option>Emma Wilson</option>
                    <option>John Smith</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Title
                  </label>
                  <input
                    type="text"
                    className="w-full p-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-fitness-primary"
                    defaultValue="Sarah's Progress Review"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Note Content
                  </label>
                  <textarea
                    rows={8}
                    className="w-full p-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-fitness-primary"
                    defaultValue="Excellent progress on weight loss goals. Lost 12 lbs in 8 weeks. Needs to focus more on strength training to maintain muscle mass during weight loss phase. Recommended increasing protein intake to 1.2g per kg body weight."
                  />
                </div>
                <div className="flex items-center space-x-2">
                  <input type="checkbox" id="pin-note" className="rounded" defaultChecked />
                  <label htmlFor="pin-note" className="text-sm text-slate-700">
                    Pin this note
                  </label>
                </div>
                <div className="flex space-x-2">
                  <Button className="flex-1">
                    Save Changes
                  </Button>
                  <Button variant="outline">
                    Cancel
                  </Button>
                </div>
              </div>
            ) : (
              <div className="text-center py-8">
                <FileText className="h-16 w-16 mx-auto mb-4 text-slate-300" />
                <h3 className="text-lg font-medium text-slate-900 mb-2">No note selected</h3>
                <p className="text-slate-600 mb-4">Select a note to edit or create a new one</p>
                <Button>
                  <Plus className="h-4 w-4 mr-2" />
                  Create New Note
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Quick Actions */}
      <Card>
        <CardHeader>
          <CardTitle>Quick Actions</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Button variant="outline" className="h-16 flex-col">
              <Plus className="h-6 w-6 mb-2" />
              <span className="text-sm">New Client Note</span>
            </Button>
            <Button variant="outline" className="h-16 flex-col">
              <FileText className="h-6 w-6 mb-2" />
              <span className="text-sm">Session Summary</span>
            </Button>
            <Button variant="outline" className="h-16 flex-col">
              <Pin className="h-6 w-6 mb-2" />
              <span className="text-sm">Important Reminder</span>
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}

export default Notes